import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserById } from "../../redux/user/userThunks";
import { clearUserData } from "../../redux/user/userSlice";
import { fetchPromoterSalesPayouts } from "../../services/promoterRequestService";
import ClubListLayer from "../Club/ClubListLayer";
import BookingListLayer from "../Booking/BookingListLayer";
import moment from "moment";
import { formatApiDate } from "../../utils/dateHelper";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Icon } from "@iconify/react";

const EMPTY_SALES_SUMMARY = {
  totalCompleted: 0,
  totalPending: 0,
  totalRefund: 0,
  totalFailed: 0,
  totalAmount: 0,
  totalRecords: 0,
  payout: {
    paidAmount: 0,
    pendingAmount: 0,
    currentAmount: 0,
    lifetimeAmount: 0,
    refundAmount: 0,
  },
  totalVenueCount: 0,
  approvedVenueCount: 0,
  pendingVenueCount: 0,
  guestListCount: 0,
  joinedGuestListCount: 0,
  scanGuestListCount: 0,
  joinedUserCount: 0,
  services: {
    club: { count: 0, sales: 0 },
    booth: { count: 0, sales: 0 },
    event: { count: 0, ticketCount: 0, sales: 0 },
    vipEntry: { count: 0, sales: 0 },
    table: { count: 0, sales: 0 },
    bottleService: {
      count: 0,
      sales: 0,
      alcoholic: { count: 0, sales: 0 },
      nonAlcoholic: { count: 0, sales: 0 },
    },
    additionalBottleService: {
      count: 0,
      sales: 0,
      alcoholic: { count: 0, sales: 0 },
      nonAlcoholic: { count: 0, sales: 0 },
    },
    scanned: { count: 0, sales: 0 },
    overallVenue: { count: 0, sales: 0 },
    totalBookings: 0,
    totalSales: 0,
  },
};

const EMPTY_SALES_FILTER = {
  fromDate: null,
  toDate: null,
};

const formatSalesFilterDate = (date) =>
  date ? moment(date).format("YYYY-MM-DD") : "";

const ViewUserPageNew = ({ roles }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { userData, loading } = useSelector((state) => state.user);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("view-user");

  const [salesTab, setSalesTab] = useState("commission");

  const [showSalesFilter, setShowSalesFilter] = useState(false);
  const [filterError, setFilterError] = useState("");

  const [salesFilter, setSalesFilter] = useState(EMPTY_SALES_FILTER);
  const [appliedSalesFilter, setAppliedSalesFilter] =
    useState(EMPTY_SALES_FILTER);

  const [salesSummary, setSalesSummary] = useState(EMPTY_SALES_SUMMARY);

  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState("");

  const getPromoterSalesPayouts = async (startDate = null, endDate = null) => {
    if (!id) return;

    try {
      setSalesLoading(true);
      setSalesError("");

      const response = await fetchPromoterSalesPayouts(id, {
        startDate: formatSalesFilterDate(startDate),
        endDate: formatSalesFilterDate(endDate),
      });

      if (response?.data?.status) {
        const data = response.data;

        setSalesSummary({
          totalCompleted: data?.summary?.totalCompleted ?? 0,
          totalPending: data?.summary?.totalPending ?? 0,
          totalRefund: data?.summary?.totalRefund ?? 0,
          totalFailed: data?.summary?.totalFailed ?? 0,
          totalAmount: data?.summary?.totalAmount ?? 0,
          totalRecords: data?.summary?.totalRecords ?? 0,

          payout: {
            paidAmount: data?.payout?.paidAmount ?? 0,
            pendingAmount: data?.payout?.pendingAmount ?? 0,
            currentAmount: data?.payout?.currentAmount ?? 0,
            lifetimeAmount: data?.payout?.lifetimeAmount ?? 0,
            refundAmount:
              data?.payout?.refundAmount ?? data?.summary?.totalRefund ?? 0,
          },

          totalVenueCount: data?.totalVenueCount ?? 0,
          approvedVenueCount: data?.approvedVenueCount ?? 0,
          pendingVenueCount: data?.pendingVenueCount ?? 0,

          guestListCount: data?.guestListCount ?? 0,
          joinedGuestListCount: data?.joinedGuestListCount ?? 0,
          scanGuestListCount: data?.scanGuestListCount ?? 0,
          joinedUserCount: data?.joinedUserCount ?? 0,

          services: {
            club: {
              count: data?.services?.club?.count ?? 0,
              commission: data?.services?.club?.commission ?? 0,
              sales: data?.services?.club?.totalBaseAmount ?? 0,
            },

            booth: {
              count: data?.services?.booth?.count ?? 0,
              commission: data?.services?.booth?.commission ?? 0,
              sales: data?.services?.booth?.totalBaseAmount ?? 0,
            },

            event: {
              count: data?.services?.event?.count ?? 0,
              ticketCount: data?.services?.event?.ticket?.count ?? 0,
              commission: data?.services?.event?.commission ?? 0,
              sales: data?.services?.event?.totalBaseAmount ?? 0,
            },

            vipEntry: {
              count: data?.services?.vipEntry?.count ?? 0,
              commission: data?.services?.vipEntry?.commission ?? 0,
              sales: data?.services?.vipEntry?.totalBaseAmount ?? 0,
            },

            table: {
              count: data?.services?.table?.count ?? 0,
              commission: data?.services?.table?.commission ?? 0,
              sales: data?.services?.table?.totalBaseAmount ?? 0,
            },

            bottleService: {
              count: data?.services?.bottleService?.count ?? 0,
              commission: data?.services?.bottleService?.commission ?? 0,
              sales: data?.services?.bottleService?.totalBaseAmount ?? 0,
              alcoholic: {
                count:
                  data?.services?.bottleService?.alcoholic?.count ?? 0,
                commission:
                  data?.services?.bottleService?.alcoholic?.commission ?? 0,
                sales:
                  data?.services?.bottleService?.alcoholic?.totalBaseAmount ?? 0,
              },

              nonAlcoholic: {
                count:
                  data?.services?.bottleService?.nonAlcoholic?.count ?? 0,
                commission:
                  data?.services?.bottleService?.nonAlcoholic?.commission ?? 0,
                sales:
                  data?.services?.bottleService?.nonAlcoholic?.totalBaseAmount ?? 0,
              },
            },

            additionalBottleService: {
              count:
                data?.services?.additionalBottleService?.count ??
                data?.additionalBottleService?.count ??
                0,
              commission:
                data?.services?.additionalBottleService?.commission ??
                data?.additionalBottleService?.commission ??
                0,
               sales:
                data?.services?.additionalBottleService?.totalBaseAmount ??
                data?.additionalBottleService?.totalBaseAmount ??
                0,
              alcoholic: {
                count:
                  data?.services?.additionalBottleService?.alcoholic?.count ??
                  data?.additionalBottleService?.alcoholic?.count ??
                  0,
                commission:
                  data?.services?.additionalBottleService?.alcoholic?.commission ??
                  data?.additionalBottleService?.alcoholic?.commission ??
                  0,
                sales:
                  data?.services?.additionalBottleService?.alcoholic?.totalBaseAmount ??
                  data?.additionalBottleService?.alcoholic?.totalBaseAmount ??
                  0,
              },
              nonAlcoholic: {
                count:
                  data?.services?.additionalBottleService?.nonAlcoholic?.count ??
                  data?.additionalBottleService?.nonAlcoholic?.count ??
                  0,
                commission:
                  data?.services?.additionalBottleService?.nonAlcoholic?.commission ??
                  data?.additionalBottleService?.nonAlcoholic?.commission ??
                  0,
                sales:
                  data?.services?.additionalBottleService?.nonAlcoholic?.totalBaseAmount ??
                  data?.additionalBottleService?.nonAlcoholic?.totalBaseAmount ??
                  0,
              },
            },

            scanned: {
              count: data?.services?.scanned?.count ?? 0,
              commission: data?.services?.scanned?.commission ?? 0,
              sales:data?.services?.scanned?.totalBaseAmount ?? 0,
            },

            overallVenue: {
              count: data?.services?.overallVenue?.count ?? 0,
              commission: data?.services?.overallVenue?.commission ?? 0,
              sales:data?.services?.overallVenue?.totalBaseAmount ?? 0,
            },

            totalBookings: data?.services?.totalBookings ?? 0,
            totalCommission: data?.services?.totalCommission ?? 0,
            totalSales: data?.services?.totalBaseAmount ?? 0,
          },
        });
      } else {
        setSalesSummary(EMPTY_SALES_SUMMARY);
        setSalesError(
          response?.data?.message || "Unable to fetch sales summary."
        );
      }
    } catch (error) {
      console.error("Promoter sales payout error:", error);

      setSalesSummary(EMPTY_SALES_SUMMARY);
      setSalesError(
        error?.response?.data?.message ||
          "Something went wrong while fetching sales summary."
      );
    } finally {
      setSalesLoading(false);
    }
  };

  // Static Commission Data
  const commissionData = [
    {
      id: 1,
      date: "2026-08-01",
      eventName: "Summer Night Party",
      venueName: "Sky Lounge",
      totalSales: 2500,
      commissionRate: 10,
      commissionAmount: 250,
      status: "Paid",
    },
    {
      id: 2,
      date: "2026-08-05",
      eventName: "Friday Night",
      venueName: "Club 21",
      totalSales: 4000,
      commissionRate: 10,
      commissionAmount: 400,
      status: "Pending",
    },
    {
      id: 3,
      date: "2026-08-10",
      eventName: "Weekend Bash",
      venueName: "The Grand Club",
      totalSales: 3500,
      commissionRate: 12,
      commissionAmount: 420,
      status: "Paid",
    },
    {
      id: 4,
      date: "2026-08-15",
      eventName: "Saturday Special",
      venueName: "Royal Lounge",
      totalSales: 5000,
      commissionRate: 10,
      commissionAmount: 500,
      status: "Pending",
    },
  ];

  // Static Bottle Service Data
  const bottleServiceData = [
    {
      id: 1,
      date: "2026-08-02",
      eventName: "Saturday Party",
      venueName: "Sky Lounge",
      bottles: 5,
      bottlePrice: 300,
      totalAmount: 1500,
      commissionAmount: 150,
      status: "Paid",
    },
    {
      id: 2,
      date: "2026-08-06",
      eventName: "VIP Night",
      venueName: "Club 21",
      bottles: 8,
      bottlePrice: 350,
      totalAmount: 2800,
      commissionAmount: 280,
      status: "Pending",
    },
    {
      id: 3,
      date: "2026-08-12",
      eventName: "Weekend Celebration",
      venueName: "The Grand Club",
      bottles: 8,
      bottlePrice: 400,
      totalAmount: 2400,
      commissionAmount: 240,
      status: "Paid",
    },
    {
      id: 4,
      date: "2026-08-18",
      eventName: "Saturday Special",
      venueName: "Royal Lounge",
      bottles: 10,
      bottlePrice: 300,
      totalAmount: 3000,
      commissionAmount: 300,
      status: "Pending",
    },
  ];

  const handleOpenSalesFilter = () => {
    setSalesFilter({
      fromDate: appliedSalesFilter.fromDate,
      toDate: appliedSalesFilter.toDate,
    });
    setFilterError("");
    setShowSalesFilter(true);
  };

  const handleCloseSalesFilter = () => {
    setFilterError("");
    setShowSalesFilter(false);
  };

  const handleStartDateChange = (date) => {
    setFilterError("");
    setSalesFilter((prev) => {
      const next = { ...prev, fromDate: date };
      if (date && prev.toDate && moment(date).isAfter(moment(prev.toDate), "day")) {
        next.toDate = null;
      }
      return next;
    });
  };

  const handleEndDateChange = (date) => {
    setFilterError("");
    if (
      date &&
      salesFilter.fromDate &&
      moment(date).isBefore(moment(salesFilter.fromDate), "day")
    ) {
      setFilterError("Start date cannot be greater than end date.");
      return;
    }
    setSalesFilter((prev) => ({ ...prev, toDate: date }));
  };

  const handleResetSalesFilter = async () => {
    setFilterError("");
    setSalesFilter(EMPTY_SALES_FILTER);
    setAppliedSalesFilter(EMPTY_SALES_FILTER);
    await getPromoterSalesPayouts(null, null);
    setShowSalesFilter(false);
  };

  const handleApplySalesFilter = async () => {
    const { fromDate, toDate } = salesFilter;

    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      setFilterError("Please select both start date and end date.");
      return;
    }

    if (
      fromDate &&
      toDate &&
      moment(fromDate).isAfter(moment(toDate), "day")
    ) {
      setFilterError("Start date cannot be greater than end date.");
      return;
    }

    setFilterError("");
    setAppliedSalesFilter({ fromDate, toDate });
    await getPromoterSalesPayouts(fromDate, toDate);
    setShowSalesFilter(false);
  };

  useEffect(() => {
    dispatch(fetchUserById({ id, navigate }));
    return () => {
      dispatch(clearUserData());
    };
  }, [id, navigate, dispatch]);

  useEffect(() => {
    if (roles === "promoter" && id) {
      getPromoterSalesPayouts(null, null);
    }
  }, [id, roles]);

  const calculateAge = (dob) => {
    if (!dob) return null;

    // Parse as local calendar date to avoid timezone shifting DOB back a day
    const dobStr = String(dob).split("T")[0];
    const [year, month, day] = dobStr.split("-").map(Number);
    if (!year || !month || !day) return null;

    const today = new Date();
    let age = today.getFullYear() - year;
    const monthDiff = today.getMonth() + 1 - month;
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
      age--;
    }
    return age >= 0 ? age : null;
  };

  const SummaryCard = ({
    title,
    value,
    icon,
    prefix = "",
    color = "primary",
    subtitle,
  }) => {
    const colorMap = {
      primary: {
        bg: "#eef4ff",
        text: "#487fff",
      },
      success: {
        bg: "#eafaf1",
        text: "#22c55e",
      },
      warning: {
        bg: "#fff7e6",
        text: "#f59e0b",
      },
      danger: {
        bg: "#fff0f0",
        text: "#ef4444",
      },
      info: {
        bg: "#eaf8ff",
        text: "#0ea5e9",
      },
      purple: {
        bg: "#f3edff",
        text: "#8b5cf6",
      },
    };

    const theme = colorMap[color] || colorMap.primary;

    return (
      <div className="col-xl-3 col-lg-4 col-md-6 mb-3">
        <div
          className="card border-0 shadow-sm h-100"
          style={{
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div
                  className="text-muted mb-2"
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  {title}
                </div>

                {salesLoading ? (
                  <div className="spinner-border spinner-border-sm text-primary" />
                ) : (
                  <h6
                    className="mb-1"
                    style={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#1f2937",
                    }}
                  >
                    {prefix}
                    {Number(value || 0).toLocaleString()}
                  </h6>
                )}

                {subtitle && (
                  <small
                    className="text-muted"
                    style={{ fontSize: "11px" }}
                  >
                    {subtitle}
                  </small>
                )}
              </div>

              {icon && (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "10px",
                    backgroundColor: theme.bg,
                    color: theme.text,
                    flexShrink: 0,
                  }}
                >
                  <Icon icon={icon} width="20" height="20" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-md-12">
        <div className="card h-100">
          <div className="card-body p-24">
            {/* Alerts */}
            {successMessage && (
              <SuccessAlert
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
            {errorMessage && (
              <DengerAlert
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}

            {/* Tab Navigation */}
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="user-view-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${activeTab === "view-user" ? "active" : ""
                    }`}
                  id="tab-view-user"
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("view-user")}
                >
                  User Profile
                </button>
              </li>

              {roles === "promoter" && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link d-flex align-items-center px-24 ${activeTab === "view-sales" ? "active" : ""
                      }`}
                    id="tab-view-sales"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab("view-sales")}
                  >
                    Sales Details
                  </button>
                </li>
              )}

              {/* Show Host Event tab only if role = host */}
              {roles === "host" && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link d-flex align-items-center px-24 ${activeTab === "view-host-events" ? "active" : ""
                      }`}
                    id="tab-view-host-events"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab("view-host-events")}
                  >
                    Hosting Details
                  </button>
                </li>
              )}

              {/* Show Booking tab only if role = user */}
              {roles === "user" && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link d-flex align-items-center px-24 ${activeTab === "view-booking" ? "active" : ""
                      }`}
                    id="tab-view-booking"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab("view-booking")}
                  >
                    Booking Details
                  </button>
                </li>
              )}

              {roles === "promoter" && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link d-flex align-items-center px-24 ${activeTab === "view-booking" ? "active" : ""
                      }`}
                    id="tab-view-booking"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab("view-booking")}
                  >
                    Booking Details
                  </button>
                </li>
              )}
            </ul>

            {/* Tab Content */}
            <div className="tab-content" id="user-tab-content">
              {/* User Profile Tab */}
              <div
                className={`tab-pane fade ${activeTab === "view-user" ? "show active" : ""
                  }`}
                id="tab-content-view-user"
                role="tabpanel"
              >
                <div className="row">
                  <p
                    style={{ fontWeight: "bold", textDecoration: "underline" }}
                  >
                    User Details
                  </p>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Name:
                    </label>
                    {userData?.fullName || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Email:
                    </label>
                    {userData?.email || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">Age:</label>
                    {calculateAge(userData?.dateOfBirth) || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Role:
                    </label>
                    {userData?.currentRole
                      ? userData.currentRole.charAt(0).toUpperCase() + userData.currentRole.slice(1).toLowerCase()
                      : "N/A"}

                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Status:
                    </label>
                    {userData?.status
                      ? userData.status.charAt(0).toUpperCase() + userData.status.slice(1).toLowerCase()
                      : "N/A"}

                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Login Type:
                    </label>
                    {userData?.loginType
                      ? userData.loginType.charAt(0).toUpperCase() +
                      userData.loginType.slice(1).toLowerCase()
                      : "N/A"}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Email Verified At:
                    </label>
                    {userData?.emailVerify
                      ? formatApiDate(userData.emailVerify)
                      : "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Date Of Birth:
                    </label>
                    {userData?.dateOfBirth
                      ? formatApiDate(userData.dateOfBirth)
                      : "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Address:
                    </label>
                    {userData?.address || "N/A"}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold me-2 mb-0">
                      Phone Number:
                    </label>
                    {userData?.phone || "N/A"}
                  </div>
                  {roles === "promoter" && (
                    <>
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Verified Status:
                        </label>
                        {userData?.verifiedstatus
                          ? userData.verifiedstatus.charAt(0).toUpperCase() + userData.verifiedstatus.slice(1).toLowerCase()
                          : "N/A"}

                      </div>
                     

                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold me-2 mb-0">
                          Promoter Code:
                        </label>
                        {userData?.refralCode || "N/A"}
                      </div>

                      {userData?.verifiedstatus === "reject" && (
                        <div className="col-md-4 mb-3">
                          <label className="form-label fw-bold me-2 mb-0">
                            Reject Reason:
                          </label>
                          {userData?.rejectreson || "N/A"}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {userData?.currentRole === "host" && (
                  <div>
                    <div className="row">
                      <p
                        style={{
                          fontWeight: "bold",
                          textDecoration: "underline",
                        }}
                      >
                        Business Details
                      </p>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label fw-bold me-2 mb-0">
                            Business Name:
                          </label>
                          {userData?.businessName || "N/A"}
                        </div>
                        <div className="col-md-8 mb-3">
                          <label className="form-label fw-bold me-2 mb-0">
                            Business Discription:
                          </label>
                          {userData?.businessDiscription || "N/A"}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label fw-bold me-2 mb-0">
                            Website URL:
                          </label>
                          {userData?.websiteUrl || "N/A"}
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label fw-bold me-2 mb-0">
                            Instagram URL:
                          </label>
                          {userData?.instagramUrl || "N/A"}
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label fw-bold me-2 mb-0">
                            Facebook URL:
                          </label>
                          {userData?.facebookUrl || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* User Images Section */}
                <div className="col-md-12 mt-4">
                  <div className="row g-3">
                    {/* Profile Picture */}
                    {userData?.profilePicture && (
                      <div className="col-md-3">
                        <label className="form-label fw-bold mb-1">
                          Profile Picture:
                        </label>
                        <img
                          src={userData.profilePicture}
                          alt="Profile"
                          className="img-fluid rounded shadow-sm"
                          style={{
                            width: "100%",
                            maxHeight: "180px",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            window.open(userData.profilePicture, "_blank")
                          }
                        />
                      </div>
                    )}

                    {/* Business License */}
                    {userData?.userDocument && (
                      <div className="col-md-3">
                        <label className="form-label fw-bold mb-1">
                          Business License:
                        </label>
                        {(() => {
                          const fileUrl = userData.userDocument;
                          const fileExtension = fileUrl
                            .split(".")
                            .pop()
                            .toLowerCase();
                          const isImage = [
                            "jpg",
                            "jpeg",
                            "png",
                            "gif",
                            "webp",
                          ].includes(fileExtension);

                          return isImage ? (
                            // ✅ Image preview (click opens in new tab)
                            <img
                              src={fileUrl}
                              alt="Business License"
                              className="img-fluid rounded shadow-sm"
                              style={{
                                width: "100%",
                                maxHeight: "180px",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={() => window.open(fileUrl, "_blank")}
                            />
                          ) : (
                            // ✅ PDF file (shows icon, click opens in new tab)
                            <div
                              className="d-flex flex-column align-items-center justify-content-center border rounded shadow-sm p-3"
                              style={{ height: "180px", cursor: "pointer" }}
                              onClick={() => window.open(fileUrl, "_blank")}
                            >
                              <Icon icon="mdi:file-pdf-box" width="48" height="48" className="text-danger" />
                              <span className="mt-2 fw-bold text-danger">
                                View PDF
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Business Picture */}
                    {userData?.businessPicture && (
                      <div className="col-md-3">
                        <label className="form-label fw-bold mb-1">
                          Business Picture:
                        </label>
                        <img
                          src={userData.businessPicture}
                          alt="Business Picture"
                          className="img-fluid rounded shadow-sm"
                          style={{
                            width: "100%",
                            maxHeight: "180px",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            window.open(userData.businessPicture, "_blank")
                          }
                        />
                      </div>
                    )}

                    {/* Business Banner */}
                    {userData?.businessBanner && (
                      <div className="col-md-3">
                        <label className="form-label fw-bold mb-1">
                          Business Banner:
                        </label>
                        <img
                          src={userData.businessBanner}
                          alt="Business Banner"
                          className="img-fluid rounded shadow-sm"
                          style={{
                            width: "100%",
                            maxHeight: "180px",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            window.open(userData.businessBanner, "_blank")
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Fallback */}
                  {!userData?.profilePicture &&
                    !userData?.userDocument &&
                    !userData?.businessPicture &&
                    !userData?.businessBanner && (
                      <p className="text-muted mt-2">No images available.</p>
                    )}
                </div>
              </div>

              {/* Sales Details Tab - Promoter */}
              {roles === "promoter" && (
                <div
                  className={`tab-pane fade ${activeTab === "view-sales" ? "show active" : ""
                    }`}
                  id="tab-content-view-sales"
                  role="tabpanel"
                >
                  {/* Sales Details Header */}
                  <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <div>
                      {appliedSalesFilter.fromDate &&
                        appliedSalesFilter.toDate && (
                          <small className="text-muted">
                            Showing data from{" "}
                            <strong>
                              {moment(appliedSalesFilter.fromDate).format(
                                "DD MMM YYYY"
                              )}
                            </strong>{" "}
                            to{" "}
                            <strong>
                              {moment(appliedSalesFilter.toDate).format(
                                "DD MMM YYYY"
                              )}
                            </strong>
                          </small>
                        )}
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center gap-2"
                      onClick={handleOpenSalesFilter}
                    >
                      <Icon icon="mdi:filter-variant" width="18" height="18" />
                      Filter
                    </button>
                  </div>

                  {salesError && (
                    <DengerAlert
                      message={salesError}
                      onClose={() => setSalesError("")}
                    />
                  )}

                  {/* ================= SALES DASHBOARD ================= */}
                  <div className="mt-4">

                    {/* ================= PAYOUT OVERVIEW ================= */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h6
                            className="mb-1 fw-bold"
                            style={{
                              fontSize: "16px",
                              color: "#1f2937",
                            }}
                          >
                            Payout Overview
                          </h6>

                          <small className="text-muted">
                            Earnings and payout status
                          </small>
                        </div>
                      </div>

                      <div className="row">
                        <SummaryCard
                          title="Paid"
                          value={salesSummary?.payout?.paidAmount}
                          prefix="$"
                          icon="mdi:check-circle"
                          color="success"
                          subtitle="Successfully paid"
                        />

                        <SummaryCard
                          title="Pending"
                          value={salesSummary?.payout?.pendingAmount}
                          prefix="$"
                          icon="mdi:clock"
                          color="warning"
                          subtitle="Awaiting payout"
                        />

                        <SummaryCard
                          title="Available"
                          value={salesSummary?.payout?.currentAmount}
                          prefix="$"
                          icon="mdi:wallet"
                          color="primary"
                          subtitle="Currently available"
                        />

                        <SummaryCard
                          title="Lifetime Amount"
                          value={salesSummary?.payout?.lifetimeAmount}
                          prefix="$"
                          icon="mdi:currency-usd"
                          color="purple"
                          subtitle="Lifetime earnings"
                        />
                      </div>
                    </div>


                    {/* ================= VENUE PERFORMANCE ================= */}
                    <div className="mb-4">
                      <div className="mb-3 d-flex justify-content-between align-items-start">
                        <div>
                          <h6
                            className="mb-1 fw-bold"
                            style={{
                              fontSize: "16px",
                              color: "#1f2937",
                            }}
                          >
                            Venue Performance
                          </h6>

                          <small className="text-muted">
                            Promoter venue activity and revenue
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn btn-primary d-flex align-items-center gap-2"
                          onClick={() =>
                            navigate(`/promoter/venues/${id}`)
                          }
                        >
                          <span>View All</span>
                          <Icon icon="mdi:arrow-right" width="18" height="18" />
                        </button>
                      </div>

                      <div className="row">
                        <SummaryCard
                          title="Total"
                          value={
                            Number(salesSummary?.approvedVenueCount || 0) +
                            Number(salesSummary?.pendingVenueCount || 0)
                          }
                          icon="mdi:office-building"
                          color="primary"
                          subtitle="Approved and pending venues"
                        />

                        <SummaryCard
                          title="Approved"
                          value={salesSummary?.approvedVenueCount}
                          icon="mdi:check-circle"
                          color="success"
                          subtitle="Approved venues"
                        />

                        <SummaryCard
                          title="Pending Approval"
                          value={salesSummary?.pendingVenueCount}
                          icon="mdi:timer-sand"
                          color="warning"
                          subtitle="Awaiting approval"
                        />
                      </div>
                    </div>


                    {/* ================= JOINED TOTAL USER COUNT ================= */}
                    <div className="mb-4">
                      <div className="mb-3 d-flex justify-content-between align-items-start">
                        <div>
                          <h6
                            className="mb-1 fw-bold"
                            style={{
                              fontSize: "16px",
                              color: "#1f2937",
                            }}
                          >
                            Joined User
                          </h6>
                        </div>

                        <button
                          type="button"
                          className="btn btn-primary d-flex align-items-center gap-2"
                          onClick={() =>
                            navigate(`/promoter/joined-users/${id}`)
                          }
                        >
                          <span>View All</span>
                          <Icon icon="mdi:arrow-right" width="18" height="18" />
                        </button>
                      </div>

                      <div className="row">
                        <SummaryCard
                          title="Joined Total User Count"
                          value={salesSummary?.joinedUserCount}
                          icon="mdi:account-multiple-check"
                          color="primary"
                          subtitle="Total joined users"
                        />
                      </div>
                    </div>

                    {/* ================= GUEST LIST PERFORMANCE ================= */}
                    <div className="mb-4">
                      <div className="mb-3">
                        <h6
                          className="mb-1 fw-bold"
                          style={{
                            fontSize: "16px",
                            color: "#1f2937",
                          }}
                        >
                          Guest List Performance
                        </h6>

                        <small className="text-muted">
                          Guest registrations and check-ins
                        </small>
                      </div>

                      <div className="row">
                        {/* <SummaryCard
                          title="Guest Lists"
                          value={salesSummary?.guestListCount}
                          icon="mdi:account-group"
                          color="primary"
                          subtitle="Total guest lists"
                        /> */}

                        <SummaryCard
                          title="Joined"
                          value={salesSummary?.joinedGuestListCount}
                          icon="mdi:account-check"
                          color="info"
                          subtitle="Guests joined"
                        />

                        <SummaryCard
                          title="Checked In"
                          value={salesSummary?.scanGuestListCount}
                          icon="mdi:barcode-scan"
                          color="success"
                          subtitle="Guests scanned"
                        />
                      </div>
                    </div>


                    {/* ================= BOOKING PERFORMANCE ================= */}
                    {/* <div className="mb-4">
                      <div className="mb-3">
                        <h6
                          className="mb-1 fw-bold"
                          style={{
                            fontSize: "16px",
                            color: "#1f2937",
                          }}
                        >
                          Booking Performance
                        </h6>

                        <small className="text-muted">
                          Booking volume and transaction status
                        </small>
                      </div>

                      <div className="row">
                        <SummaryCard
                          title="Total Bookings"
                          value={salesSummary?.services?.totalBookings}
                          icon="mdi:calendar-check"
                          color="primary"
                          subtitle="All bookings"
                        />

                        <SummaryCard
                          title="Booking Revenue"
                          value={salesSummary?.services?.totalSales}
                          prefix="$"
                          icon="mdi:chart-bar"
                          color="purple"
                          subtitle="Total booking revenue"
                        />
                      </div>
                    </div> */}


                    {/* ================= SERVICE PERFORMANCE ================= */}
                    <div className="mb-4">
                      <div className="mb-3 d-flex justify-content-between align-items-start">
                        <div>
                          <h6
                            className="mb-1 fw-bold"
                            style={{
                              fontSize: "16px",
                              color: "#1f2937",
                            }}
                          >
                            Service Performance
                          </h6>

                          <small className="text-muted">
                            Sales generated by service category
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn btn-primary d-flex align-items-center gap-2"
                          onClick={() => navigate(`/promoter-bottle-service/${id}`)}
                        >
                          <Icon icon="mdi:history" width="18" height="18" />
                          <span>History</span>
                          <Icon icon="mdi:arrow-right" width="18" height="18" />
                        </button>
                      </div>
                                            <div className="row">
                        <SummaryCard
                          title="Total Bookings"
                          value={salesSummary?.services?.totalBookings}
                          icon="mdi:calendar-check"
                          color="primary"
                          subtitle="All bookings"
                        />

                        <SummaryCard
                          title="Promoter Commission"
                          value={salesSummary?.services?.totalCommission}
                          prefix="$"
                          icon="mdi:chart-bar"
                          color="purple"
                          subtitle="Total promoter commission"
                        />
                        <SummaryCard
                          title="Total Sales"
                          value={salesSummary?.services?.totalSales}
                          prefix="$"
                          icon="mdi:chart-bar"
                          color="purple"
                          subtitle="Total sales"
                        />
                      </div>
                      <h6>Promoter Commissions</h6>
                      <div className="row">
                        {/* Club */}
                        {/* <SummaryCard
                          title="Club"
                          value={salesSummary?.services?.club?.commission}
                          prefix="$"
                          icon="mdi:office-building"
                          color="primary"
                          subtitle={`${salesSummary?.services?.club?.count || 0} bookings`}
                        /> */}

                        {/* Booth */}
                        <SummaryCard
                          title="Booth"
                          value={salesSummary?.services?.booth?.commission}
                          prefix="$"
                          icon="mdi:view-grid"
                          color="info"
                          subtitle={`${salesSummary?.services?.booth?.count || 0} bookings`}
                        />

                        {/* Event */}
                        <SummaryCard
                          title="Event"
                          value={salesSummary?.services?.event?.commission}
                          prefix="$"
                          icon="mdi:calendar-star"
                          color="purple"
                          subtitle={`${salesSummary?.services?.event?.count || 0} bookings, ${salesSummary?.services?.event?.ticketCount || 0} tickets`}
                        />

                        {/* VIP Entry */}
                        <SummaryCard
                          title="VIP Entry"
                          value={salesSummary?.services?.vipEntry?.commission}
                          prefix="$"
                          icon="mdi:star"
                          color="warning"
                          subtitle={`${salesSummary?.services?.vipEntry?.count || 0} bookings`}
                        />

                        {/* Table */}
                        <SummaryCard
                          title="Table"
                          value={salesSummary?.services?.table?.commission}
                          prefix="$"
                          icon="mdi:table-furniture"
                          color="success"
                          subtitle={`${salesSummary?.services?.table?.count || 0} bookings`}
                        />

                        {/* Bottle Service */}
                        <SummaryCard
                          title="Bottle Service"
                          value={salesSummary?.services?.bottleService?.commission}
                          prefix="$"
                          icon="mdi:glass-cocktail"
                          color="danger"
                          subtitle={`${salesSummary?.services?.bottleService?.count || 0} bookings`}
                        />

                        {/* Alcoholic */}
                        {/* <SummaryCard
                          title="Alcoholic"
                          value={
                            salesSummary?.services?.bottleService?.alcoholic?.commission
                          }
                          prefix="$"
                          icon="mdi:glass-wine"
                          color="danger"
                          subtitle={`${salesSummary?.services?.bottleService?.alcoholic?.count || 0} items`}
                        /> */}

                        {/* Non-Alcoholic */}
                        {/* <SummaryCard
                          title="Non-Alcoholic"
                          value={
                            salesSummary?.services?.bottleService?.nonAlcoholic?.sales
                          }
                          prefix="$"
                          icon="mdi:glass-cocktail"
                          color="info"
                          subtitle={`${salesSummary?.services?.bottleService?.nonAlcoholic?.count || 0} items`}
                        /> */}

                        {/* Scanned */}
                        <SummaryCard
                          title="Scanned"
                          value={salesSummary?.services?.scanned?.commission}
                          prefix="$"
                          icon="mdi:barcode-scan"
                          color="info"
                          subtitle={`${salesSummary?.services?.scanned?.count || 0} scanned`}
                        />

                        {/* Total Venue */}
                        <SummaryCard
                          title="Total Venue"
                          value={salesSummary?.services?.overallVenue?.commission}
                          prefix="$"
                          icon="mdi:city"
                          color="primary"
                          subtitle={`${salesSummary?.services?.overallVenue?.count || 0} venues`}
                        />
                      </div>
                        <h6>Total Sales</h6>
                      <div className="row">
                        {/* Club */}
                        {/* <SummaryCard
                          title="Club"
                          value={salesSummary?.services?.club?.sales}
                          prefix="$"
                          icon="mdi:office-building"
                          color="primary"
                          subtitle={`${salesSummary?.services?.club?.count || 0} bookings`}
                        /> */}

                        {/* Booth */}
                        <SummaryCard
                          title="Booth"
                          value={salesSummary?.services?.booth?.sales}
                          prefix="$"
                          icon="mdi:view-grid"
                          color="info"
                          subtitle={`${salesSummary?.services?.booth?.count || 0} bookings`}
                        />

                        {/* Event */}
                        <SummaryCard
                          title="Event"
                          value={salesSummary?.services?.event?.sales}
                          prefix="$"
                          icon="mdi:calendar-star"
                          color="purple"
                          subtitle={`${salesSummary?.services?.event?.count || 0} bookings, ${salesSummary?.services?.event?.ticketCount || 0} tickets`}
                        />

                        {/* VIP Entry */}
                        <SummaryCard
                          title="VIP Entry"
                          value={salesSummary?.services?.vipEntry?.sales}
                          prefix="$"
                          icon="mdi:star"
                          color="warning"
                          subtitle={`${salesSummary?.services?.vipEntry?.count || 0} bookings`}
                        />

                        {/* Table */}
                        <SummaryCard
                          title="Table"
                          value={salesSummary?.services?.table?.sales}
                          prefix="$"
                          icon="mdi:table-furniture"
                          color="success"
                          subtitle={`${salesSummary?.services?.table?.count || 0} bookings`}
                        />

                        {/* Bottle Service */}
                        <SummaryCard
                          title="Bottle Service"
                          value={salesSummary?.services?.bottleService?.sales}
                          prefix="$"
                          icon="mdi:glass-cocktail"
                          color="danger"
                          subtitle={`${salesSummary?.services?.bottleService?.count || 0} bookings`}
                        />

                        {/* Alcoholic */}
                        {/* <SummaryCard
                          title="Alcoholic"
                          value={
                            salesSummary?.services?.bottleService?.alcoholic?.sales
                          }
                          prefix="$"
                          icon="mdi:glass-wine"
                          color="danger"
                          subtitle={`${salesSummary?.services?.bottleService?.alcoholic?.count || 0} items`}
                        /> */}

                        {/* Non-Alcoholic */}
                        {/* <SummaryCard
                          title="Non-Alcoholic"
                          value={
                            salesSummary?.services?.bottleService?.nonAlcoholic?.sales
                          }
                          prefix="$"
                          icon="mdi:glass-cocktail"
                          color="info"
                          subtitle={`${salesSummary?.services?.bottleService?.nonAlcoholic?.count || 0} items`}
                        /> */}

                        {/* Scanned */}
                        <SummaryCard
                          title="Scanned"
                          value={salesSummary?.services?.scanned?.sales}
                          prefix="$"
                          icon="mdi:barcode-scan"
                          color="info"
                          subtitle={`${salesSummary?.services?.scanned?.count || 0} scanned`}
                        />

                        {/* Total Venue */}
                        <SummaryCard
                          title="Total Venue"
                          value={salesSummary?.services?.overallVenue?.sales}
                          prefix="$"
                          icon="mdi:city"
                          color="primary"
                          subtitle={`${salesSummary?.services?.overallVenue?.count || 0} venues`}
                        />
                      </div>
                    </div>


                    {/* ================= ADDITIONAL BOTTLE SERVICES ================= */}
                    <div className="mb-4">
                      <div className="mb-3 d-flex justify-content-between align-items-start">
                        <div>
                          <h6
                            className="mb-1 fw-bold"
                            style={{
                              fontSize: "16px",
                              color: "#1f2937",
                            }}
                          >
                            Additional Bottle Services
                          </h6>

                          <small className="text-muted">
                            Bottle sales and category breakdown
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn btn-primary d-flex align-items-center gap-2"
                          onClick={() =>
                            navigate(`/promoter/additional-bottle-service/${id}`)
                          }
                        >
                          <span>View All</span>
                          <Icon icon="mdi:arrow-right" width="18" height="18" />
                        </button>
                      </div>

                      <div className="row">
                        {/* Additional Alcoholic */}
                        <SummaryCard
                          title="Additional Bottle Service Total Promoter Commission"
                          value={
                            salesSummary?.services?.additionalBottleService.commission
                          }
                          prefix="$"
                          icon="mdi:plus-circle"
                          color="warning"
                          subtitle={`${salesSummary?.services?.additionalBottleService.count || 0} bookings`}
                        />
                         <SummaryCard
                          title="Additional Bottle Service Total Sales"
                          value={
                            salesSummary?.services?.additionalBottleService.sales
                          }
                          prefix="$"
                          icon="mdi:plus-circle"
                          color="warning"
                          subtitle={`${salesSummary?.services?.additionalBottleService.count || 0} bookings`}
                        />


                        {/* Additional Non-Alcoholic */}
                        {/* <SummaryCard
                          title="Additional Non-Alcoholic"
                          value={
                            salesSummary?.services?.additionalBottleService
                              ?.nonAlcoholic?.sales
                          }
                          prefix="$"
                          icon="mdi:plus-circle"
                          color="success"
                          subtitle={`${salesSummary?.services?.additionalBottleService?.nonAlcoholic?.count || 0} items`}
                        /> */}
                      </div>
                    </div>

                  </div>
                </div>
              )}
              {/* Booking Details Tab (for user and promoter) */}
              {(roles === "user" || roles === "promoter") && (
                <div
                  className={`tab-pane fade ${activeTab === "view-booking" ? "show active" : ""
                    }`}
                  id="tab-content-view-booking"
                  role="tabpanel"
                >
                  <div className="row">
                    <BookingListLayer
                      userId={id}
                      isPromoterView={roles === "promoter"}
                    />
                  </div>
                </div>
              )}

              {/* Host Events Tab (for host) */}
              {roles === "host" && (
                <div
                  className={`tab-pane fade ${activeTab === "view-host-events" ? "show active" : ""
                    }`}
                  id="tab-content-view-host-events"
                  role="tabpanel"
                >
                  <div className="row">
                    <ClubListLayer userId={id} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Sales Filter Modal */}
      {showSalesFilter &&
        createPortal(
          <div
            className="modal fade show"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 1055,
            }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={handleCloseSalesFilter}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "420px" }}
            >
              <div className="modal-content">
                <div className="modal-header py-3">
                  <h5 className="modal-title">Filter Sales Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleCloseSalesFilter}
                  ></button>
                </div>

                <div className="modal-body">
                  {filterError && (
                    <div className="alert alert-danger py-2" role="alert">
                      {filterError}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-bold">Start Date</label>
                    <DatePicker
                      selected={salesFilter.fromDate}
                      onChange={handleStartDateChange}
                      maxDate={salesFilter.toDate || undefined}
                      placeholderText="dd-mm-yyyy"
                      dateFormat="dd-MM-yyyy"
                      className="form-control w-100"
                      wrapperClassName="w-100"
                      isClearable
                      shouldCloseOnSelect
                      onKeyDown={(e) => e.preventDefault()}
                      popperPlacement="bottom-start"
                      popperProps={{ strategy: "fixed" }}
                      popperClassName="sales-filter-datepicker-popper"
                    />
                  </div>

                  <div className="mb-1">
                    <label className="form-label fw-bold">End Date</label>
                    <DatePicker
                      selected={salesFilter.toDate}
                      onChange={handleEndDateChange}
                      minDate={salesFilter.fromDate || undefined}
                      placeholderText="dd-mm-yyyy"
                      dateFormat="dd-MM-yyyy"
                      className="form-control w-100"
                      wrapperClassName="w-100"
                      isClearable
                      shouldCloseOnSelect
                      onKeyDown={(e) => e.preventDefault()}
                      popperPlacement="bottom-start"
                      popperProps={{ strategy: "fixed" }}
                      popperClassName="sales-filter-datepicker-popper"
                    />
                  </div>
                </div>

                <div className="modal-footer py-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleResetSalesFilter}
                    disabled={salesLoading}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleApplySalesFilter}
                    disabled={salesLoading}
                  >
                    {salesLoading ? "Applying..." : "Apply Filter"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ViewUserPageNew;
